// Card color mappings for Majhong suits
export const CARD_COLORS = {
  wan: '#e74c3c', // Red for Wan (Characters)
  tong: '#3498db', // Blue for Tong (Circles/Dots)
  tiao: '#2ecc71', // Green for Tiao (Bamboos)
};

// Sort hand by color then value
export function sortByColor(cards) {
  const sorted = [...cards].sort((a, b) => {
    // 1. Sort by type (number < word < wild < skip)
    const typeOrder = { number: 0, word: 1, wild: 2, skip: 3 };
    if (typeOrder[a.type] !== typeOrder[b.type]) {
      return typeOrder[a.type] - typeOrder[b.type];
    }

    // 2. If both are numbers, sort by suit (color) then value
    if (a.type === 'number') {
      const colorOrder = { wan: 0, tong: 1, tiao: 2 };
      if (colorOrder[a.color] !== colorOrder[b.color]) {
        return colorOrder[a.color] - colorOrder[b.color];
      }
      return a.value - b.value;
    }

    // 3. If both are words, sort by traditional word order
    if (a.type === 'word') {
      const wordOrder = { east: 0, south: 1, west: 2, north: 3, zhong: 4, fa: 5, bai: 6 };
      return wordOrder[a.color] - wordOrder[b.color];
    }

    return 0; // Same type and identical values
  });

  return sorted;
}

// Sort hand by number then color
export function sortByNumber(cards) {
  const sorted = [...cards].sort((a, b) => {
    // 1. Sort by type (number < word < wild < skip)
    const typeOrder = { number: 0, word: 1, wild: 2, skip: 3 };
    if (typeOrder[a.type] !== typeOrder[b.type]) {
      return typeOrder[a.type] - typeOrder[b.type];
    }

    // 2. If both are numbers, sort by value first, then suit (color)
    if (a.type === 'number') {
      if (a.value !== b.value) return a.value - b.value;
      const colorOrder = { wan: 0, tong: 1, tiao: 2 };
      return colorOrder[a.color] - colorOrder[b.color];
    }

    // 3. If both are words, sort by traditional word order
    if (a.type === 'word') {
      const wordOrder = { east: 0, south: 1, west: 2, north: 3, zhong: 4, fa: 5, bai: 6 };
      return wordOrder[a.color] - wordOrder[b.color];
    }

    return 0;
  });

  return sorted;
}

// Legacy function for compatibility
export function sortHand(cards, sortMode = 'color') {
  return sortMode === 'number' ? sortByNumber(cards) : sortByColor(cards);
}

// Get card display color
export function getCardColor(card) {
  if (card.type === 'wild') {
    return 'linear-gradient(135deg, #e74c3c, #f39c12, #2ecc71, #3498db)';
  }
  if (card.type === 'skip') {
    return '#c0392b';
  }
  return CARD_COLORS[card.color] || '#ffffff';
}

// Get background color class based on card properties
export function getCardBgClass(card) {
  if (!card) return 'bg-[#f4efe6]';

  // Refined ivory/bone Mahjong aesthetics for fallbacks
  if (card.type === 'wild') return 'bg-gradient-to-br from-amber-100 to-[#e8dcb8]';
  if (card.type === 'skip') return 'bg-zinc-300';

  // All standard suits and words get a natural ivory/bone background
  return 'bg-[#f4efe6]';
}

// Get text color class based on card properties
export function getCardTextColor(card) {
  if (!card) return 'text-zinc-800';

  if (card.type === 'wild') return 'text-amber-700 text-shadow-sm';
  if (card.type === 'skip') return 'text-zinc-700';

  // Honor tiles have specific traditional engraving colors
  if (card.type === 'word') {
    if (['zhong', 'east', 'south', 'west', 'north'].includes(card.color)) return 'text-red-800';
    if (card.color === 'fa') return 'text-green-800';
    return 'text-blue-800'; // Default for Bai
  }

  // Number suits map to their traditional dark ink colors
  const textMap = {
    wan: 'text-red-800',
    tong: 'text-blue-800',
    tiao: 'text-green-800',
    // Fallbacks
    red: 'text-red-800',
    blue: 'text-blue-800',
    green: 'text-green-800',
    yellow: 'text-amber-700'
  };

  return textMap[card.color] || 'text-zinc-800';
}

// Format card for display (Mahjong symbols)
export function formatCard(card) {
  if (card.type === 'wild') {
    return 'WILD';
  }
  if (card.type === 'skip') {
    return 'SKIP';
  }

  if (card.type === 'word') {
    const wordMap = {
      east: '东风',
      south: '南风',
      west: '西风',
      north: '北风',
      zhong: '红中',
      fa: '发财',
      bai: '白板'
    };
    return wordMap[card.color] || '字';
  }

  const chineseNumbers = ['一', '二', '三', '四', '五', '六', '七', '八', '九'];
  const numStr = chineseNumbers[card.value - 1] || card.value;

  if (card.color === 'wan') return `${numStr}万`;
  if (card.color === 'tong') return `${numStr}筒`;
  if (card.color === 'tiao') return `${numStr}条`;

  return card.value.toString();
}

// Get genuine Mahjong PNG image path
export function getCardImage(card) {
  // Wild and Skip now use mahjong-style tile images
  if (card.type === 'wild') return '/mahjong/wild.png';
  if (card.type === 'skip') return '/mahjong/skip.png';

  if (card.type === 'word') {
    const wordImageMap = {
      east: '/mahjong/28.png',
      south: '/mahjong/29.png',
      west: '/mahjong/30.png',
      north: '/mahjong/31.png',
      bai: '/mahjong/32.png',
      zhong: '/mahjong/33.png',
      fa: '/mahjong/34.png'
    };
    return wordImageMap[card.color] || null;
  }

  if (card.type === 'number') {
    const val = card.value;
    if (card.color === 'wan') {
      return `/mahjong/${18 + val}.png`; // 19 to 27
    } else if (card.color === 'tong') {
      return `/mahjong/${val}.png`; // 1 to 9
    } else if (card.color === 'tiao') {
      return `/mahjong/${9 + val}.png`; // 10 to 18
    }
  }

  return null;
}


// Get card point value
export function getCardPoints(card) {
  if (card.type === 'wild' || card.type === 'skip') {
    return 25;
  }
  if (card.value >= 10) {
    return 10;
  }
  return 5;
}

// Calculate total points for a hand
export function calculateHandPoints(cards) {
  return cards.reduce((sum, card) => sum + getCardPoints(card), 0);
}

// Format score with comma separators
export function formatScore(score) {
  return score.toLocaleString();
}

// Group cards by value (for set detection)
export function groupByValue(cards) {
  const groups = {};
  for (const card of cards) {
    if (card.type === 'number') {
      if (!groups[card.value]) {
        groups[card.value] = [];
      }
      groups[card.value].push(card);
    }
  }
  return groups;
}

// Group cards by color
export function groupByColor(cards) {
  const groups = {};
  for (const card of cards) {
    if (card.color) {
      if (!groups[card.color]) {
        groups[card.color] = [];
      }
      groups[card.color].push(card);
    }
  }
  return groups;
}

// Find potential runs in cards
export function findPotentialRuns(cards) {
  const numberCards = cards.filter(c => c.type === 'number');
  const values = numberCards.map(c => c.value).sort((a, b) => a - b);
  const uniqueValues = [...new Set(values)];

  const runs = [];
  let currentRun = [uniqueValues[0]];

  for (let i = 1; i < uniqueValues.length; i++) {
    if (uniqueValues[i] === uniqueValues[i - 1] + 1) {
      currentRun.push(uniqueValues[i]);
    } else {
      if (currentRun.length >= 3) {
        runs.push([...currentRun]);
      }
      currentRun = [uniqueValues[i]];
    }
  }

  if (currentRun.length >= 3) {
    runs.push(currentRun);
  }

  return runs;
}

// Check if a card can be added to a run
export function canExtendRun(card, runCards) {
  if (card.type === 'wild') return true;
  if (card.type !== 'number') return false;

  const values = runCards
    .filter(c => c.type === 'number')
    .map(c => c.value)
    .sort((a, b) => a - b);

  if (values.length === 0) return true;

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  return card.value === minVal - 1 || card.value === maxVal + 1;
}

// Check if a card can be added to a set
export function canExtendSet(card, setCards) {
  if (card.type === 'wild') return true;
  if (card.type !== 'number') return false;

  const numberCards = setCards.filter(c => c.type === 'number');
  if (numberCards.length === 0) return true;

  return card.value === numberCards[0].value;
}

// Get suit name for display
export function getColorName(color) {
  if (!color) return 'Colorless';

  const colors = {
    wan: '万子 (Wan)',
    tong: '筒子 (Tong)',
    tiao: '条子 (Tiao)',
    east: '东',
    south: '南',
    west: '西',
    north: '北',
    zhong: '红中',
    fa: '发财',
    bai: '白板',
    red: 'Red',
    blue: 'Blue',
    green: 'Green',
    yellow: 'Yellow'
  };

  return colors[color] || color;
}

// Check if cards form a valid color group
export function isValidColorGroup(cards) {
  const numberCards = cards.filter(c => c.type === 'number');
  if (numberCards.length === 0) return true; // All wilds

  const colors = numberCards.map(c => c.color);
  const uniqueColors = [...new Set(colors)];

  return uniqueColors.length === 1;
}

// Check if a card can be hit on a phase group (client-side validation)
export function canHitOnPhase(card, existingGroup, groupType) {
  if (!card || !existingGroup || !groupType) {
    return { canHit: false, reason: 'Invalid parameters' };
  }

  if (card.type === 'skip') {
    return { canHit: false, reason: 'Skip cards cannot be used to hit' };
  }

  if (card.type === 'wild') {
    return { canHit: true }; // Wilds can always hit on any group
  }

  switch (groupType.type) {
    case 'set':
    case 'colorSet': {
      // Can add to set if same number AND SAME COLOR
      const numberCards = existingGroup.filter(c => c.type === 'number');
      if (numberCards.length === 0) {
        return { canHit: true }; // All wilds, any number works
      }
      const setValue = numberCards[0].value;
      const setColor = numberCards[0].color;
      
      if (card.value !== setValue || card.color !== setColor) {
        return { canHit: false, reason: 'Card must match the set exactly (Number AND Suit)' };
      }
      return { canHit: true };
    }

    case 'run':
    case 'colorRun':
    case 'oddRun':
    case 'evenRun':
    case 'oddColorRun':
    case 'evenColorRun': {
      // Calculate effective values based on card positions in the run
      // Wilds take on values based on their position relative to number cards
      const numberCards = existingGroup.filter(c => c.type === 'number');
      const numberValues = numberCards.map(c => c.value).sort((a, b) => a - b);

      const isOddEvenRun = groupType.type.toLowerCase().includes('odd') || groupType.type.toLowerCase().includes('even');
      const step = isOddEvenRun ? 2 : 1;

      if (numberValues.length === 0) {
        // All wilds - any valid number can extend
        if (card.value < 1 || card.value > 9) {
          return { canHit: false, reason: 'Card value out of range (must be 1-9)' };
        }
        return { canHit: true };
      }

      // Calculate effective value for each card in the run based on position
      // This properly handles wilds at the beginning, middle, or end
      const effectiveValues = [];

      // Find the first number card index to anchor the run
      let firstNumIdx = -1;
      for (let i = 0; i < existingGroup.length; i++) {
        if (existingGroup[i].type === 'number') {
          if (firstNumIdx === -1) firstNumIdx = i;
        }
      }

      // Calculate effective values based on positions
      const firstNumValue = existingGroup[firstNumIdx].value;
      for (let i = 0; i < existingGroup.length; i++) {
        // Effective value = first number card's value - (distance from first number card)
        const effectiveVal = firstNumValue - (firstNumIdx - i) * step;
        effectiveValues.push(effectiveVal);
      }

      const effectiveMinVal = Math.min(...effectiveValues);
      const effectiveMaxVal = Math.max(...effectiveValues);

      // Check if this value is already represented in the run
      if (effectiveValues.includes(card.value)) {
        return { canHit: false, reason: 'Value already in run' };
      }

      // Check if card extends the run at either end ONLY
      if (card.value !== effectiveMinVal - step && card.value !== effectiveMaxVal + step) {
        return { canHit: false, reason: 'Card must extend the run at either end' };
      }

      // Check bounds
      if (card.value < 1 || card.value > 9) {
        return { canHit: false, reason: 'Card value out of range (must be 1-9)' };
      }

      // Check odd/even constraints
      if (groupType.type.includes('odd') && card.value % 2 === 0) {
        return { canHit: false, reason: 'Must be an odd number' };
      }
      if (groupType.type.includes('even') && card.value % 2 !== 0) {
        return { canHit: false, reason: 'Must be an even number' };
      }

      // Check color constraints
      if (groupType.type.includes('Color') || groupType.type.includes('color')) {
        const colors = numberCards.map(c => c.color);
        const groupColor = [...new Set(colors)][0];
        if (groupColor && card.color !== groupColor) {
          return { canHit: false, reason: 'Card must match the run color' };
        }
      }

      return { canHit: true };
    }

    case 'color': {
      // Can add if same color
      const numberCards = existingGroup.filter(c => c.type === 'number');
      if (numberCards.length === 0) {
        return { canHit: true };
      }
      const groupColor = numberCards[0].color;
      if (card.color !== groupColor) {
        return { canHit: false, reason: 'Card must match the color group' };
      }
      return { canHit: true };
    }

    case 'terminals': {
      if (card.type === 'number' && (card.value === 1 || card.value === 9)) return { canHit: true };
      return { canHit: false, reason: 'Must be 1 or 9' };
    }
    case 'anyOdd': {
      if (card.type === 'number' && card.value % 2 !== 0) return { canHit: true };
      return { canHit: false, reason: 'Must be an odd number' };
    }
    case 'anyEven': {
      if (card.type === 'number' && card.value % 2 === 0) return { canHit: true };
      return { canHit: false, reason: 'Must be an even number' };
    }
    case 'anyWord':
    case 'wordSet': {
      if (card.type === 'word') {
        if (groupType.type === 'wordSet') {
          const wordCards = existingGroup.filter(c => c.type === 'word');
          if (wordCards.length > 0 && wordCards[0].color !== card.color) {
            return { canHit: false, reason: 'Must be the exact same word card' };
          }
        }
        return { canHit: true };
      }
      return { canHit: false, reason: 'Must be a word card' };
    }

    default:
      return { canHit: false, reason: 'Unknown group type' };
  }
}

const CardHelpers = {
  CARD_COLORS,
  sortHand,
  sortByColor,
  sortByNumber,
  getCardColor,
  getCardBgClass,
  getCardTextColor,
  formatCard,
  getCardPoints,
  calculateHandPoints,
  formatScore,
  groupByValue,
  groupByColor,
  findPotentialRuns,
  canExtendRun,
  canExtendSet,
  getColorName,
  isValidColorGroup,
  canHitOnPhase,
  getCardImage
};

export default CardHelpers;
